import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import { useCustomerAddresses } from "@/api/use-customer-addresses";
import { Label } from "@/components/ui/label";

interface SelectedAddress {
  addressComponents: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode?: string;
    country: string;
  };
  location: {
    lat: number;
    lng: number;
  };
}

const newAddressSchema = z.object({
  label: z.string().min(1, "Label is required"),
  name: z.string().min(1, "Name is required"),
  phone: z.string().min(1, "Phone number is required"),
  pickupNotes: z.string().optional(),
  address: z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    province: z.string().min(1),
    postalCode: z.string().optional(),
    country: z.string().min(1),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});

type NewAddressSchema = z.infer<typeof newAddressSchema>;

interface AddAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddAddressDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddAddressDialogProps) {
  const { addresses, addAddress: addAddressMutation } = useCustomerAddresses();
  const [selectedNewAddress, setSelectedNewAddress] =
    useState<SelectedAddress | null>(null);

  const form = useForm<NewAddressSchema>({
    resolver: zodResolver(newAddressSchema),
    defaultValues: {
      label: "",
      name: "",
      phone: "",
      pickupNotes: "",
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[600px] sm:w-full">
        <DialogHeader>
          <DialogTitle>Add New Address</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Address</Label>
          <AutocompleteAddress
            onSelectedAddress={(selectedAddress) => {
              setSelectedNewAddress(selectedAddress);
              if (selectedAddress) {
                form.setValue("address", {
                  line1: selectedAddress.addressComponents.line1,
                  line2: selectedAddress.addressComponents.line2,
                  city: selectedAddress.addressComponents.city,
                  province: selectedAddress.addressComponents.state,
                  postalCode:
                    selectedAddress.addressComponents.postalCode ?? "",
                  country: selectedAddress.addressComponents.country,
                  location: selectedAddress.location,
                });
              }
            }}
            placeholder="Search address"
          />
          {selectedNewAddress && (
            <Form {...form}>
              <form
                className="flex flex-col gap-4 mt-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit((values) => {
                    void addAddressMutation.mutate(
                      {
                        isPrimary: addresses?.length === 0,
                        label: values.label,
                        name: values.name,
                        phone: values.phone,
                        pickupNotes: values.pickupNotes,
                        address: values.address,
                      },
                      {
                        onSuccess: () => {
                          setSelectedNewAddress(null);
                          onOpenChange(false);
                          form.reset();
                          onSuccess?.();
                        },
                      }
                    );
                  })(e);
                }}
              >
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Label</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Address label (e.g. Home, Work)"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Contact name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          placeholder="Contact phone number"
                          defaultCountry="CA"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pickupNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Notes</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Pickup notes (optional)"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit">Add New Address</Button>
              </form>
            </Form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
